<template>
  <article>
    <div>
      <div
        class="
          bg-gradient-to-r
          from-indigo-400
          via-light-blue-500
          to-purple-500
          h-32
          w-full
        "
      ></div>
      <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="-mt-4 sm:-mt-8 sm:flex sm:items-end sm:space-x-5">
          <div
            class="
              mt-6
              sm:flex-1
              sm:min-w-0
              sm:flex
              sm:items-center
              sm:justify-end
              sm:space-x-6
              sm:pb-1
            "
          >
            <div class="mt-6 min-w-0 flex-1">
              <h1
                class="
                  text-2xl
                  font-bold
                  text-gray-900
                  truncate
                  flex
                  items-center
                "
              >
                {{ equipment.data.identifier }}
                <span
                  :class="
                    'h-5 w-5 bg-' +
                    statusColor(equipment.data.status) +
                    '-100 rounded-full flex items-center justify-center ml-1'
                  "
                  aria-hidden="true"
                >
                  <span
                    :class="
                      'h-2 w-2 bg-' +
                      statusColor(equipment.data.status) +
                      '-400 rounded-full'
                    "
                  ></span>
                </span>
              </h1>
            </div>
            <div
              class="
                mt-6
                flex flex-col
                justify-stretch
                space-y-3
                sm:flex-row sm:space-y-0 sm:space-x-4
              "
            >
              <ui-button
                focusColorStyling="focus:ring-red-500"
                backgroundStyling="bg-white hover:bg-red-50"
                borderStyling="border border-red-300"
                textColorStyling="text-red-700 hover:text-red-500"
                type="button"
                class="flex justify-center w-full"
                @click="confirmingEquipmentDeletion = true"
              >
                <TrashIcon class="-ml-1 mr-2 h-5 w-5 text-red-400" />

                {{ t("delete") }}</ui-button
              >
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabs -->
    <div class="mt-6 sm:mt-2 2xl:mt-5">
      <div class="border-b border-gray-200">
        <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav class="-mb-px flex space-x-8" aria-label="Tabs">
            <Link
              :href="
                route('equipment.edit', {
                  equipment: equipment.data.id,
                  search: $page.props.filters.search,
                })
              "
              :class="[
                {
                  'border-light-blue-500 text-light-blue-600':
                    tab == 'information',
                },
                {
                  'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300':
                    tab != 'information',
                },
              ]"
              class="
                cursor-pointer
                whitespace-nowrap
                py-4
                px-1
                border-b-2
                font-medium
                text-sm
              "
              aria-current="page"
            >
              {{ t("information") }}
            </Link>
            <Link
              :href="
                route('equipment.equipment-calibrations.index', {
                  equipment: equipment.data.id,
                  search: $page.props.filters.search,
                })
              "
              :class="[
                {
                  'border-light-blue-500 text-light-blue-600':
                    tab == 'calibrations',
                },
                {
                  'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300':
                    tab != 'calibrations',
                },
              ]"
              class="
                cursor-pointer
                whitespace-nowrap
                py-4
                px-1
                border-b-2
                font-medium
                text-sm
              "
              aria-current="page"
            >
              {{ t("calibrations") }}
            </Link>
            <Link
              :href="
                route('equipment.equipment-revisions.index', {
                  equipment: equipment.data.id,
                  search: $page.props.filters.search,
                })
              "
              :class="[
                {
                  'border-light-blue-500 text-light-blue-600':
                    tab == 'revisions',
                },
                {
                  'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300':
                    tab != 'revisions',
                },
              ]"
              class="
                cursor-pointer
                whitespace-nowrap
                py-4
                px-1
                border-b-2
                font-medium
                text-sm
              "
              aria-current="page"
            >
              {{ t("revisions") }}
            </Link>
          </nav>
        </div>
      </div>
    </div>

    <!-- Description list -->
    <div>
      <equipment-information
        v-if="tab == 'information'"
        :equipment="equipment"
      />

      <equipment-calibrations
        :equipment="equipment"
        :equipmentCalibrations="equipmentCalibrations"
        v-if="tab == 'calibrations'"
      />

      <equipment-revisions
        :equipment="equipment"
        :equipmentRevisions="equipmentRevisions"
        v-if="tab == 'revisions'"
      />
    </div>

    <ui-modal
      :show="confirmingEquipmentDeletion"
      @close="confirmingEquipmentDeletion = false"
    >
      <div class="sm:flex sm:items-start">
        <div
          class="
            mx-auto
            flex-shrink-0 flex
            items-center
            justify-center
            h-12
            w-12
            rounded-full
            bg-red-100
            sm:mx-0 sm:h-10 sm:w-10
          "
        >
          <ExclamationIcon class="h-6 w-6 text-red-600" />
        </div>
        <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
          <h3 class="text-lg leading-6 font-medium text-gray-900">
            {{ t("confirm_equipment_deletion_title") }}
          </h3>
          <div class="mt-2">
            <p class="text-sm text-gray-500">
              {{ t("confirm_equipment_deletion_text") }}
            </p>
          </div>
        </div>
      </div>
      <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
        <ui-button
          backgroundStyling="bg-red-600 hover:bg-red-700"
          focusColorStyling="focus:ring-red-500"
          type="button"
          @click="deleteEquipment"
          class="flex justify-center w-full sm:w-auto ml-0 sm:ml-3"
          >{{ t("delete_equipment") }}</ui-button
        >
        <ui-button
          backgroundStyling="bg-white"
          borderStyling="border border-gray-300"
          textColorStyling="text-gray-700 hover:text-gray-500"
          type="button"
          @click="confirmingEquipmentDeletion = false"
          class="flex justify-center w-full sm:w-auto mt-3 sm:mt-0"
          >{{ t("nevermind") }}</ui-button
        >
      </div>
    </ui-modal>
  </article>
</template>

<script>
import { ExclamationIcon } from "@heroicons/vue/outline";
import { TrashIcon } from "@heroicons/vue/solid";
import EquipmentInformation from "./EquipmentInformation";
import EquipmentCalibrations from "./EquipmentCalibrations";
import EquipmentRevisions from "./EquipmentRevisions";
import { useI18n } from "vue-i18n";

export default {
  components: {
    EquipmentInformation,
    EquipmentCalibrations,
    EquipmentRevisions,
    TrashIcon,
    ExclamationIcon,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: {
    equipment: { type: Object, required: true },

    equipmentCalibrations: { type: Object, required: false },

    equipmentRevisions: { type: Object, required: false },

    tab: { type: String, required: true },
  },

  data() {
    return {
      confirmingEquipmentDeletion: false,
      form: this.$inertia.form({
        _method: "DELETE",
      }),
    };
  },

  methods: {
    deleteEquipment() {
      this.form.delete(
        this.route("equipment.destroy", {
          equipment: this.equipment.data.id,
          search: this.$page.props.filters.search,
        }),
        {
          preserveScroll: true,
        }
      );
    },

    statusColor(status) {
      let colors = {
        available: "green",
        occupied: "indigo",
        revision: "yellow",
        calibration: "yellow",
        unavailable: "red",
      };

      if (status in colors) {
        return colors[status];
      }

      return "gray";
    },
  },
};
</script>

<i18n>
{
  "es": {
    "cancel": "Cancelar",
    "delete": "Eliminar",
    "confirm_equipment_deletion_title": "¿Seguro que deseas eleminar?",
    "confirm_equipment_deletion_text": "Al eliminar el equipo, todos los recursos relacionados con el equipo permanecerán activos, sin embargo no podrán ser relacionados nuevos recursos.",
    "delete_equipment": "Eliminar equipo",
    "nevermind": "Mejor no",
    "add": "Agregar",
    "new_calibration": "Nueva calibración",
    "new_revision": "Nueva verificación",
    "information": "Información",
    "calibrations": "Calibraciones",
    "revisions": "Verificaciones intermedias"
  }
}
</i18n>
